/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { renderStatus } from 'components/v2/cell-renderers/status';
import {
  canCriarRequisicaoEntrega,
  getEntregaStatusLabel,
  getRequisicaoItems,
  hasItensPendentes,
  TIPO_ENTREGA_OPTIONS,
} from './statusLabels';
import moment from 'moment';

const tipoLabel = (codigo) =>
  (TIPO_ENTREGA_OPTIONS.find((o) => o.id === codigo) || {}).nome || codigo || '—';

const EntregaAcoes = ({
  entrega,
  onEditar,
  onCancelar,
  onDespachar,
  onFinalizar,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const preparando = entrega.status === 'PREPARANDO';
  const enviado = entrega.status === 'ENVIADO';
  if (entrega.status === 'CANCELADA' || entrega.status === 'ENTREGUE') return null;

  const mais = [];
  if (preparando) {
    mais.push({
      label: 'Cancelar entrega',
      danger: true,
      onClick: () => onCancelar(entrega),
    });
  }

  return (
    <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" justifyContent="flex-end">
      {preparando && (
        <Tooltip title="Marca que o pacote saiu. A entrega passa a em trânsito e os itens ficam enviados.">
          <Button size="small" variant="contained" onClick={() => onDespachar(entrega)}>
            Despachar
          </Button>
        </Tooltip>
      )}
      {enviado && (
        <Button size="small" variant="contained" onClick={() => onFinalizar(entrega)}>
          Finalizar
        </Button>
      )}
      {(preparando || enviado) && (
        <Button size="small" variant="outlined" onClick={() => onEditar(entrega)}>
          Editar
        </Button>
      )}
      {mais.length > 0 && (
        <>
          <Button size="small" variant="text" onClick={(event) => setAnchorEl(event.currentTarget)}>
            Mais
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
          >
            {mais.map((item) => (
              <MenuItem
                key={item.label}
                onClick={() => {
                  setAnchorEl(null);
                  item.onClick();
                }}
                sx={item.danger ? { color: 'error.main' } : undefined}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );
};

EntregaAcoes.propTypes = {
  entrega: PropTypes.object.isRequired,
  onEditar: PropTypes.func.isRequired,
  onCancelar: PropTypes.func.isRequired,
  onDespachar: PropTypes.func.isRequired,
  onFinalizar: PropTypes.func.isRequired,
};

const itemNames = (entrega, requisicao) => {
  const ids = entrega.itemIds || [];
  const items = getRequisicaoItems(requisicao);
  return items
    .filter((item) => ids.includes(item.id) || item.entregaId === entrega.id)
    .map((item) => item.nomeItem)
    .join(', ') || '—';
};

const EntregasTab = ({
  entregas,
  requisicao,
  canManage,
  onCriar,
  onEditar,
  onCancelar,
  onDespachar,
  onFinalizar,
}) => {
  const canCriar = canManage && canCriarRequisicaoEntrega(requisicao);
  const pendentes = canManage && hasItensPendentes(requisicao);
  const criarButton = canCriar ? (
    <Button
      size="small"
      variant="contained"
      onClick={onCriar}
      sx={{ alignSelf: 'flex-start' }}
    >
      Criar entrega
    </Button>
  ) : null;
  const hint = pendentes ? (
    <Typography variant="body2" color="text.secondary">
      Decida todos os itens na aba Itens antes de criar uma entrega.
    </Typography>
  ) : canCriar && (!entregas || !entregas.length) ? (
    <Typography variant="body2" color="text.secondary">
      Agrupe itens aprovados ou para pedido para despachar.
    </Typography>
  ) : null;

  if (!entregas || !entregas.length) {
    return (
      <Stack spacing={1.5} alignItems="flex-start">
        {criarButton}
        {hint}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {criarButton}
      {hint}
      {entregas.map((entrega) => {
        const historico = entrega.historicoRastreamento || entrega.historico || [];
        return (
          <Box
            key={entrega.id}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
            }}
          >
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {renderStatus({
                    value: entrega.status,
                    label: getEntregaStatusLabel(entrega.status),
                  })}
                  <Typography variant="body2">
                    {tipoLabel(entrega.tipoEntrega)}
                  </Typography>
                </Stack>
                {canManage && (
                  <EntregaAcoes
                    entrega={entrega}
                    onEditar={onEditar}
                    onCancelar={onCancelar}
                    onDespachar={onDespachar}
                    onFinalizar={onFinalizar}
                  />
                )}
              </Stack>
              <Typography variant="body2">
                Itens:
                {' '}
                {itemNames(entrega, requisicao)}
              </Typography>
              {entrega.transportadora && (
                <Typography variant="caption">
                  Transportadora:
                  {' '}
                  {entrega.transportadora}
                </Typography>
              )}
              {(entrega.colaboradorNome
                || (entrega.colaborador && entrega.colaborador.nome)) && (
                <Typography variant="caption">
                  Colaborador:
                  {' '}
                  {entrega.colaboradorNome
                    || (entrega.colaborador && entrega.colaborador.nome)}
                </Typography>
              )}
              {entrega.dataEsperadaEntrega && (
                <Typography variant="caption">
                  Data esperada:
                  {' '}
                  {moment(entrega.dataEsperadaEntrega).format('DD/MM/YYYY')}
                </Typography>
              )}
              {entrega.codigoRastreamento && (
                <Typography variant="caption">
                  Rastreio:
                  {' '}
                  {entrega.codigoRastreamento}
                </Typography>
              )}
              {historico.length > 0 && (
                <Stack spacing={0.5} sx={{ pt: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    Timeline
                  </Typography>
                  {historico.map((evento) => (
                    <Typography key={evento.id || evento.dataEvento} variant="caption">
                      {evento.dataEvento
                        ? moment(evento.dataEvento).format('DD/MM/YYYY HH:mm')
                        : '—'}
                      {' · '}
                      {evento.statusRastreamento}
                      {evento.localizacao ? ` · ${evento.localizacao}` : ''}
                      {evento.descricao ? ` — ${evento.descricao}` : ''}
                    </Typography>
                  ))}
                </Stack>
              )}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
};

EntregasTab.propTypes = {
  entregas: PropTypes.array,
  requisicao: PropTypes.object,
  canManage: PropTypes.bool,
  onCriar: PropTypes.func.isRequired,
  onEditar: PropTypes.func.isRequired,
  onCancelar: PropTypes.func.isRequired,
  onDespachar: PropTypes.func.isRequired,
  onFinalizar: PropTypes.func.isRequired,
};

EntregasTab.defaultProps = {
  entregas: [],
  requisicao: {},
  canManage: false,
};

export default EntregasTab;
